import type { ReactNode } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/molecules/dialog";
import { DIRECTIVE_CONFIG } from "../core/config";
import {
  CONSOLE_DIALOG_BODY_CLASS,
  CONSOLE_DIALOG_TITLE_CLASS,
} from "@/lib/ui/consoleDialogTypography";
import { cn } from "@/lib/utils";

export interface DirectiveReferenceModalProps {
  children: ReactNode;
}

/** Build component breakdown: Directive, Variable, and each choice slot. */
const getComponentBreakdown = (
  label: string,
  choiceSlots: { label: string }[],
): string => {
  const parts = [`Directive: @${label}`, "Variable: [name]"];
  for (const slot of choiceSlots) {
    parts.push(`${slot.label}: [value]`);
  }
  return parts.join(", ");
};

export const DirectiveReferenceModal = ({
  children,
}: DirectiveReferenceModalProps) => (
  <Dialog>
    <DialogTrigger asChild>{children}</DialogTrigger>
    <DialogContent
      surface="console"
      className="max-h-[min(90vh,560px)] overflow-auto"
    >
      <DialogHeader>
        <DialogTitle className={CONSOLE_DIALOG_TITLE_CLASS}>
          Directive variable reference
        </DialogTitle>
        <DialogDescription
          id="rte-directive-ref-desc"
          className={CONSOLE_DIALOG_BODY_CLASS}
        >
          In Markdown mode type{" "}
          <code className="rounded-sm bg-black/5 px-1">{`{{@directive/var}}`}</code>{" "}
          or with choices{" "}
          <code className="rounded-sm bg-black/5 px-1">{`{{@directive/var:c1:c2}}`}</code>
          .
        </DialogDescription>
      </DialogHeader>
      <ul
        className={cn(CONSOLE_DIALOG_BODY_CLASS, "mt-3 space-y-3")}
        aria-labelledby="rte-directive-ref-desc"
      >
        {DIRECTIVE_CONFIG.map((d) => (
          <li
            key={d.key}
            className="rounded-md border border-border bg-black/5 px-3 py-2"
          >
            <span className="font-medium">
              <b>@{d.label}</b>
            </span>
            <p className="mt-1 text-foreground/70">
              {getComponentBreakdown(d.label, d.choiceSlots)}
            </p>
            {d.exampleNotation != null && (
              <p className="mt-1 font-mono text-xs text-foreground/80">
                {d.exampleNotation}
              </p>
            )}
            <p className="mt-1 text-foreground/70">
              <b>Variable types</b>: {d.variableTypes.join(", ")}.
            </p>
            {d.exampleSparx != null && d.exampleRendered != null && (
              <div
                className="
                  mt-2 rounded-sm border border-border/50 bg-background/50 px-2
                  py-1.5
                "
              >
                <p className="text-foreground/70">
                  <b>Example</b>:{" "}
                  <code className="font-mono text-xs break-all">
                    {d.exampleSparx}
                  </code>
                </p>
                <p className="mt-1 text-foreground/70">→ {d.exampleRendered}</p>
              </div>
            )}
          </li>
        ))}
      </ul>
    </DialogContent>
  </Dialog>
);
